import { ContactStatusOptions } from '@amzn/aet-contactrecommendation-kit';
import {
  FilterKeyType,
  FilterStatusType,
  UniversalFilterValue,
  DisplayMessage,
  FilterDisplay,
  FilterKey,
  OUTLIER_DEFINITIONS,
  OutlierType,
  FilterValueType,
} from './types/types';
import { getAmazonWeekAsGGGGDashWW } from '@amzn/aet-generic-kit';
import moment from 'moment';

export const DISPLAY_STRINGS = {
  FILTER_PREFIX: {
    VIEW_DATA_BY: {
      id: 'cs_perfportal_filter_prefix_view_data_by',
      defaultMessage: 'View data by',
    },
    WEEK: {
      id: 'cs_perfportal_filter_prefix_week',
      defaultMessage: 'Week',
    },
    WEEKS: {
      id: 'cs_perfportal_filter_prefix_weeks',
      defaultMessage: 'Weeks',
    },
    CATEGORY: {
      id: 'cs_perfportal_filter_prefix_category',
      defaultMessage: 'Category',
    },
    ASSOCIATE: {
      id: 'cs_perfportal_filter_prefix_associate',
      defaultMessage: 'Associate',
    },
    TM: {
      id: 'cs_perfportal_filter_prefix_tm',
      defaultMessage: 'TM',
    },
    STATUS: {
      id: 'cs_perfportal_filter_prefix_status',
      defaultMessage: 'Status',
    },
  },
  STATUS: {
    PENDING: {
      id: 'cs_perfportal_filter_status_pending',
      defaultMessage: 'Pending',
    },
    REVIEWED: {
      id: 'cs_perfportal_filter_status_reviewed',
      defaultMessage: 'Reviewed',
    },
    DELIVERED: {
      id: 'cs_perfportal_filter_status_delivered',
      defaultMessage: 'Delivered',
    },
    DRAFT: {
      id: 'cs_perfportal_filter_status_draft',
      defaultMessage: 'Draft',
    },
    ALL: {
      id: 'cs_perfportal_filter_all',
      defaultMessage: 'All',
    },
  },
  FILTER: {
    ALL_CATEGORIES: {
      id: 'cs_perfportal_filter_all',
      defaultMessage: 'All',
    },
    ALL_ASSOCIATES: {
      id: 'cs_perfportal_filter_all',
      defaultMessage: 'All',
    },
    CURRENT_WEEK: {
      id: 'cs_perfportal_filter_current_week',
      defaultMessage: 'Current Week ({week})',
    },
    WEEK: {
      // Will always resolve be default as this is a placeholder translation
      id: 'cs_perfportal_filter_week',
      defaultMessage: '{week}',
    },
    WEEKS: {
      id: 'cs_perfportal_filter_week_range',
      defaultMessage: 'Week range',
    },
    ASSOCIATE_NAME: {
      id: 'cs_perfportal_filter_associate_name',
      defaultMessage: '{name}',
    },
    FORMAT: {
      id: 'cs_perfportal_filter_format',
      defaultMessage: '{filterPrefix}: {value}',
    },
  },
  EXPANDER_TAB_COUNT: {
    TOTAL: {
      id: 'cs_perfportal_expander_tab_count_total',
      defaultMessage: '{total} Total Contacts',
    },
    PENDING: {
      id: 'cs_perfportal_expander_tab_count_pending',
      defaultMessage: '{total} Pending Contacts',
    },
    REMAINING_REVIEWS: {
      id: 'cs_perfportal_expander_tab_count_remaining_reviews',
      defaultMessage: '{total} remaining reviews required',
    },
    REMAINING_REVIEW_SINGULAR: {
      id: 'cs_perfportal_expander_tab_count_remaining_review_singular',
      defaultMessage: '{total} remaining review required',
    },
    DRAFT: {
      id: 'cs_perfportal_expander_tab_count_draft',
      defaultMessage: '{total} Draft Contacts',
    },
    REVIEWED: {
      id: 'cs_perfportal_expander_tab_count_reviewed',
      defaultMessage: '{total} Reviewed Contacts',
    },
    DELIVERED: {
      id: 'cs_perfportal_expander_tab_count_delivered',
      defaultMessage: '{total} Delivered Contacts',
    },
  },
} as const;

export const getActionFromStatus = (
  status: ContactStatusOptions
): DisplayMessage => {
  switch (status) {
    case ContactStatusOptions.REVIEWED:
    case ContactStatusOptions.DELIVERED:
      return {
        id: 'cs_perfportal_action_view',
        defaultMessage: 'View',
      };
    case ContactStatusOptions.DRAFT:
      return {
        id: 'cs_perfportal_action_edit',
        defaultMessage: 'Edit',
      };
    case ContactStatusOptions.UNDEFINED:
    default:
      return {
        id: 'cs_perfportal_action_review',
        defaultMessage: 'Review',
      };
  }
};

export const getPrefixDisplay = (key: FilterKeyType): DisplayMessage => {
  const prefixMessages: Record<FilterKeyType, DisplayMessage> = {
    [FilterKey.TIME_OPTIONS]: DISPLAY_STRINGS.FILTER_PREFIX.WEEKS,
    [FilterKey.TIME_OPTION]: DISPLAY_STRINGS.FILTER_PREFIX.WEEK,
    [FilterKey.VIEW_DATA_BY]: DISPLAY_STRINGS.FILTER_PREFIX.VIEW_DATA_BY,
    [FilterKey.CATEGORY]: DISPLAY_STRINGS.FILTER_PREFIX.CATEGORY,
    [FilterKey.ASSOCIATE]: DISPLAY_STRINGS.FILTER_PREFIX.ASSOCIATE,
    [FilterKey.TM]: DISPLAY_STRINGS.FILTER_PREFIX.TM,
    [FilterKey.STATUS]: DISPLAY_STRINGS.FILTER_PREFIX.STATUS,
  };

  return prefixMessages[key];
};

export const getStatusDisplay = (status: FilterStatusType): DisplayMessage => {
  if (status === UniversalFilterValue.ALL) {
    return DISPLAY_STRINGS.STATUS.ALL;
  }

  const statusMessages: Record<ContactStatusOptions, DisplayMessage> = {
    [ContactStatusOptions.UNDEFINED]: DISPLAY_STRINGS.STATUS.PENDING,
    [ContactStatusOptions.REVIEWED]: DISPLAY_STRINGS.STATUS.REVIEWED,
    [ContactStatusOptions.DELIVERED]: DISPLAY_STRINGS.STATUS.DELIVERED,
    [ContactStatusOptions.DRAFT]: DISPLAY_STRINGS.STATUS.DRAFT,
  };

  return statusMessages[status];
};
const getOutlierDefinitionByValue = (value: OutlierType) => {
  const entry = Object.entries(OUTLIER_DEFINITIONS).find(
    ([_, def]) => def.value === value
  );
  if (!entry) {
    throw new Error(`No outlier definition found for value: ${value}`);
  }
  return entry[1];
};

export const getFilterDisplay = <K extends FilterKeyType>(
  key: K,
  value: FilterValueType[K]
): FilterDisplay => {
  const prefix = getPrefixDisplay(key);
  let valueDisplay: DisplayMessage;

  switch (key) {
    case FilterKey.STATUS:
      valueDisplay = getStatusDisplay(value as FilterStatusType);
      break;

    case FilterKey.CATEGORY:
      valueDisplay =
        value === UniversalFilterValue.ALL
          ? DISPLAY_STRINGS.FILTER.ALL_CATEGORIES
          : {
              id: getOutlierDefinitionByValue(value as OutlierType).i18nKey,
              defaultMessage: value as string,
            };
      break;

    case FilterKey.TIME_OPTION:
      const currentWeek = getAmazonWeekAsGGGGDashWW(moment());
      valueDisplay =
        value === currentWeek
          ? { ...DISPLAY_STRINGS.FILTER.CURRENT_WEEK, values: { week: value } }
          : { ...DISPLAY_STRINGS.FILTER.WEEK, values: { week: value } };
      break;

    case FilterKey.TIME_OPTIONS:
      valueDisplay = {
        ...DISPLAY_STRINGS.FILTER.WEEKS,
        values: { weeks: value },
      };
      break;

    case FilterKey.ASSOCIATE:
      valueDisplay =
        value === UniversalFilterValue.ALL
          ? DISPLAY_STRINGS.FILTER.ALL_ASSOCIATES
          : {
              ...DISPLAY_STRINGS.FILTER.ASSOCIATE_NAME,
              values: { name: value },
            };
      break;

    default:
      valueDisplay = DISPLAY_STRINGS.STATUS.PENDING;
      break;
  }

  return { prefix, value: valueDisplay };
};

export const getExpanderTabCountDisplay = (
  status: FilterStatusType,
  total: number,
  requiredReviewsRemaining?: number
): DisplayMessage => {
  if (status === UniversalFilterValue.ALL) {
    return {
      ...DISPLAY_STRINGS.EXPANDER_TAB_COUNT.TOTAL,
      values: { total },
    };
  }

  // If status is Pending and requiredReviewsRemaining is provided, use the remaining reviews display
  if (
    status === ContactStatusOptions.UNDEFINED &&
    requiredReviewsRemaining !== undefined
  ) {
    // Use singular form when count is 1
    if (requiredReviewsRemaining === 1) {
      return {
        ...DISPLAY_STRINGS.EXPANDER_TAB_COUNT.REMAINING_REVIEW_SINGULAR,
        values: { total: requiredReviewsRemaining },
      };
    } else {
      return {
        ...DISPLAY_STRINGS.EXPANDER_TAB_COUNT.REMAINING_REVIEWS,
        values: { total: requiredReviewsRemaining },
      };
    }
  }

  const countDisplays: Record<ContactStatusOptions, DisplayMessage> = {
    [ContactStatusOptions.UNDEFINED]:
      DISPLAY_STRINGS.EXPANDER_TAB_COUNT.PENDING,
    [ContactStatusOptions.REVIEWED]:
      DISPLAY_STRINGS.EXPANDER_TAB_COUNT.REVIEWED,
    [ContactStatusOptions.DELIVERED]:
      DISPLAY_STRINGS.EXPANDER_TAB_COUNT.DELIVERED,
    [ContactStatusOptions.DRAFT]: DISPLAY_STRINGS.EXPANDER_TAB_COUNT.DRAFT,
  };

  return {
    ...countDisplays[status],
    values: { total },
  };
};
